import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoadWithView } from '../../../../core/models/load.model';
import { LoadSegment } from '../../../../core/models/load-segment.model';
import { LoadPayrollSnapshot } from '../../../../core/models/payroll.model';
import { UserService } from '../../../../core/services/user.service';
import { CardComponent } from '../../../../shared/ui/layout/card/card.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { ColorVariant } from '../../../../core/enums/types';

export interface AdjustLegPayEvent {
  driverId: string;
  segment: LoadSegment | null;
}

export interface DriverPayRowView {
  key: string;
  driverId: string;
  driverDisplayName: string;
  segment: LoadSegment | null;
  legLabel: string | null;
  miles: number;
  emptyMiles: number;
  servicedStopsCount: number | null;
  basePay: number;
  stopPay: number;
  detentionPay: number;
  lumperReimbursement: number;
  deductionsTotal: number;
  totalPay: number;
  hasManualOverride: boolean;
  manualOverrideNote: string | null;
  statusLabel: string;
  statusVariant: ColorVariant;
  isSettled: boolean;
}

@Component({
  selector: 'app-load-pay-summary',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    TextComponent,
    BadgeComponent,
    ButtonComponent,
    LucideIconComponent,
  ],
  template: `
    @if (payRows().length > 0) {
      <app-card
        headerText="Driver Pay Attribution"
        size="sm"
        icon="Wallet"
        [collapsible]="true"
        [(collapsed)]="collapsed"
      >
        <div card-summary class="flex justify-between items-center text-sm">
          <app-text color="muted" size="xs">
            {{ payRows().length === 1 ? payRows()[0].driverDisplayName : payRows().length + ' driver shares' }}
          </app-text>
          <app-text weight="semibold" size="sm">{{ totalLoadDriverPay() | currency }}</app-text>
        </div>

        <div class="flex flex-col gap-3">
          @if (load()?.payrollHoldStatus === 'DISPUTED') {
            <div class="p-2.5 rounded-lg bg-surface-high border border-warning flex items-center gap-2">
              <lucide-icon name="AlertTriangle" size="15" class="text-warning shrink-0"></lucide-icon>
              <app-text size="xs" weight="medium" class="text-warning">
                Active driver pay dispute on this load — settlement held until resolved.
              </app-text>
            </div>
          }

          @for (row of payRows(); track row.key) {
            <div class="p-3 rounded-xl bg-surface border border-border flex flex-col gap-2.5">
              <div class="flex items-start justify-between gap-2">
                <div class="flex flex-col min-w-0">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <lucide-icon name="User" size="14" class="text-primary shrink-0"></lucide-icon>
                    <app-text size="sm" weight="semibold" class="truncate">
                      {{ row.driverDisplayName }}
                    </app-text>
                    @if (row.legLabel) {
                      <app-badge variant="secondary" badgeStyle="outline" size="xs" [title]="row.legLabel"></app-badge>
                    }
                    @if (row.hasManualOverride) {
                      <app-badge variant="info" badgeStyle="outline" size="xs" title="Flat Rate Override"></app-badge>
                    }
                  </div>
                  <app-text size="xs" color="muted" class="mt-0.5">
                    {{ row.miles | number }} mi
                    @if (row.emptyMiles > 0) {
                      &middot; +{{ row.emptyMiles | number }} empty mi
                    }
                    @if (row.servicedStopsCount !== null) {
                      &middot; {{ row.servicedStopsCount }} {{ row.servicedStopsCount === 1 ? 'stop' : 'stops' }} serviced
                    }
                  </app-text>
                </div>

                <div class="flex flex-col items-end shrink-0 gap-1">
                  <app-text size="sm" weight="bold" class="text-foreground">
                    {{ row.totalPay | currency }}
                  </app-text>
                  <app-badge [variant]="row.statusVariant" size="xs" [title]="row.statusLabel"></app-badge>
                </div>
              </div>

              <!-- Line-item breakdown -->
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-border text-xs">
                <div>
                  <app-text size="xs" color="muted">Base{{ row.hasManualOverride ? ' (Flat)' : '' }}</app-text>
                  <app-text size="xs" weight="semibold">{{ row.basePay | currency }}</app-text>
                </div>
                @if (row.stopPay > 0) {
                  <div>
                    <app-text size="xs" color="muted">Stop Pay</app-text>
                    <app-text size="xs" weight="semibold">{{ row.stopPay | currency }}</app-text>
                  </div>
                }
                @if (row.detentionPay > 0) {
                  <div>
                    <app-text size="xs" color="muted">Detention</app-text>
                    <app-text size="xs" weight="semibold">{{ row.detentionPay | currency }}</app-text>
                  </div>
                }
                @if (row.lumperReimbursement > 0) {
                  <div>
                    <app-text size="xs" color="muted">Lumper Reimb.</app-text>
                    <app-text size="xs" weight="semibold">{{ row.lumperReimbursement | currency }}</app-text>
                  </div>
                }
                @if (row.deductionsTotal > 0) {
                  <div>
                    <app-text size="xs" color="muted">Deductions</app-text>
                    <app-text size="xs" weight="semibold" class="text-danger">-{{ row.deductionsTotal | currency }}</app-text>
                  </div>
                }
              </div>

              @if (row.manualOverrideNote) {
                <app-text size="xs" color="muted" class="italic">
                  &ldquo;{{ row.manualOverrideNote }}&rdquo;
                </app-text>
              }

              @if (canEditLegs() && !row.isSettled) {
                <div class="flex justify-end pt-1">
                  <app-button
                    variant="ghost"
                    size="xs"
                    icon="SlidersHorizontal"
                    (click)="adjustLegPay.emit({ driverId: row.driverId, segment: row.segment })"
                  >
                    Adjust Leg Pay
                  </app-button>
                </div>
              }
            </div>
          }

          <div class="flex items-center justify-between pt-2 border-t border-border">
            <app-text size="xs" color="muted" weight="medium">Total Load Driver Pay</app-text>
            <app-text size="sm" weight="bold" class="text-primary">
              {{ totalLoadDriverPay() | currency }}
            </app-text>
          </div>
        </div>
      </app-card>
    }
  `,
})
export class LoadPaySummaryComponent {
  private readonly userService = inject(UserService);

  load = input.required<LoadWithView | null>();
  segments = input<LoadSegment[]>([]);
  canEditLegs = input<boolean>(false);

  adjustLegPay = output<AdjustLegPayEvent>();

  protected readonly collapsed = signal(false);

  protected readonly payRows = computed<DriverPayRowView[]>(() => {
    const load = this.load();
    if (!load) return [];

    const segs = this.segments();
    const snapshots = load.driverPaySnapshots || {};
    const settledMap = load.settlementIds || {};
    const isHoldDisputed = load.payrollHoldStatus === 'DISPUTED';

    const rows: DriverPayRowView[] = [];

    // 1. Build rows from driverPaySnapshots when available (authoritative backend computation)
    const snapshotEntries = Object.entries(snapshots);
    if (snapshotEntries.length > 0) {
      for (const [driverId, snap] of snapshotEntries) {
        if (!snap) continue;

        const matchingSegs = segs.filter(
          (s) => s.driverId === driverId || s.driver2Id === driverId
        );
        const primarySegId = snap.segmentIds?.[0];
        const primarySeg =
          (primarySegId ? segs.find((s) => s.id === primarySegId) : null) ||
          matchingSegs[0] ||
          null;

        const legLabel =
          snap.legSummary ||
          (segs.length > 1 && matchingSegs.length > 0
            ? matchingSegs
                .map((s) => `Leg ${s.segmentNumber} (Stops ${s.fromStopNumber}–${s.toStopNumber})`)
                .join(', ')
            : matchingSegs.length === 1 && matchingSegs[0]?.driver2Id
            ? 'Team Split'
            : null);

        const isSettled = !!(
          snap.settlementId ||
          settledMap[driverId] ||
          (matchingSegs.length > 0 &&
            matchingSegs.every((s) => !!s.settlementIds?.[driverId])) ||
          (segs.length <= 1 && load.settlementId)
        );

        const allDriverSegsCompleted =
          matchingSegs.length > 0
            ? matchingSegs.every((s) => s.status === 'completed')
            : load.status === 'DELIVERED';

        let statusLabel = 'Accruing';
        let statusVariant: ColorVariant = 'secondary';
        if (isSettled) {
          statusLabel = 'Settled';
          statusVariant = 'success';
        } else if (isHoldDisputed) {
          statusLabel = 'Disputed';
          statusVariant = 'warning';
        } else if (allDriverSegsCompleted) {
          statusLabel = 'Ready to Settle';
          statusVariant = 'info';
        }

        const hasManualOverride =
          snap.manualPayOverride != null ||
          matchingSegs.some(
            (s) => s.manualPayOverride !== null && s.manualPayOverride !== undefined
          );

        const emptyMiles = matchingSegs.reduce(
          (sum, s) => sum + (s.emptyMilesOrigin || 0) + (s.emptyMilesDestination || 0),
          0
        );

        const deductions =
          (snap.fuelAdvances ?? 0) + (snap.otherDeductions ?? 0);

        rows.push({
          key: `${driverId}-${primarySeg?.id || 'snap'}`,
          driverId,
          driverDisplayName: this.userService.getDriverDisplayName(
            driverId,
            primarySeg?.driverId === driverId
              ? primarySeg?.driverName
              : primarySeg?.driver2Name || load.driver1Name || 'Driver'
          ),
          segment: primarySeg,
          legLabel,
          miles:
            snap.actualMiles ??
            snap.estimatedMiles ??
            primarySeg?.segmentMiles ??
            load.loadedMiles ??
            0,
          emptyMiles,
          servicedStopsCount: snap.stopsCount ?? null,
          basePay: snap.basePay ?? 0,
          stopPay: snap.stopPayTotal ?? 0,
          detentionPay: snap.detentionPayTotal ?? 0,
          lumperReimbursement: snap.lumperReimbursement ?? 0,
          deductionsTotal: deductions,
          totalPay: snap.totalDriverPay ?? 0,
          hasManualOverride,
          manualOverrideNote: primarySeg?.manualPayOverrideNote || null,
          statusLabel,
          statusVariant,
          isSettled,
        });
      }
      return rows;
    }

    // 2. Fallback to legacy single-driver payrollSnapshot when driverPaySnapshots hasn't been written yet
    const legacySnap: LoadPayrollSnapshot | null | undefined = load.payrollSnapshot;
    if (legacySnap && load.driver1Id) {
      const primarySeg = segs[0] || null;
      const isSettled = !!(
        legacySnap.settlementId ||
        settledMap[load.driver1Id] ||
        load.settlementId ||
        primarySeg?.settlementIds?.[load.driver1Id]
      );
      const isReady = primarySeg ? primarySeg.status === 'completed' : load.status === 'DELIVERED';
      const deductions =
        (legacySnap.fuelAdvances ?? 0) + (legacySnap.otherDeductions ?? 0);
      const emptyMiles =
        (primarySeg?.emptyMilesOrigin || 0) + (primarySeg?.emptyMilesDestination || 0);

      rows.push({
        key: `${load.driver1Id}-legacy`,
        driverId: load.driver1Id,
        driverDisplayName: this.userService.getDriverDisplayName(
          load.driver1Id,
          load.driver1Name || 'Driver'
        ),
        segment: primarySeg,
        legLabel: segs.length > 1 && primarySeg ? `Leg ${primarySeg.segmentNumber}` : null,
        miles:
          legacySnap.actualMiles ??
          legacySnap.estimatedMiles ??
          primarySeg?.segmentMiles ??
          load.loadedMiles ??
          0,
        emptyMiles,
        servicedStopsCount: legacySnap.stopsCount ?? null,
        basePay: legacySnap.basePay ?? 0,
        stopPay: legacySnap.stopPayTotal ?? 0,
        detentionPay: legacySnap.detentionPayTotal ?? 0,
        lumperReimbursement: legacySnap.lumperReimbursement ?? 0,
        deductionsTotal: deductions,
        totalPay: legacySnap.totalDriverPay ?? 0,
        hasManualOverride:
          legacySnap.manualPayOverride != null || primarySeg?.manualPayOverride != null,
        manualOverrideNote: primarySeg?.manualPayOverrideNote || null,
        statusLabel: isSettled
          ? 'Settled'
          : isHoldDisputed
          ? 'Disputed'
          : isReady
          ? 'Ready to Settle'
          : 'Accruing',
        statusVariant: isSettled
          ? 'success'
          : isHoldDisputed
          ? 'warning'
          : isReady
          ? 'info'
          : 'secondary',
        isSettled,
      });
    }

    return rows;
  });

  protected readonly totalLoadDriverPay = computed(() =>
    Math.round(this.payRows().reduce((sum, r) => sum + (r.totalPay || 0), 0) * 100) / 100
  );
}
